import React, { useEffect, useState } from 'react';
import { FiMinus,FiPlus } from 'react-icons/fi';

import { useAgentEdit } from '@/app/agents/edit/state/agentEditContext';
import { getNodeTypeLabel } from '@/app/agents/edit/util/nodeTypeUtils';
import { InputWithTemplateVars, TemplateVarItem } from '@/components/inputWithTemplateVars/inputWithTemplateVars';
import { Text } from '@/components/typography/text';
import {
  AgentConfigStepAction,
  AgentConfigStepApiAction,
  AgentConfigStepApiActionHeaders,
  AgentConfigStepInputs,
  AgentConfigStepTypeEnum
} from '@/types/agent';


type Props = {
  apiAction: AgentConfigStepApiAction;
  editable: boolean;
  inputs: AgentConfigStepInputs;
  onActionUpdate: <T extends AgentConfigStepAction>( action: T ) => void;
  stepId: string;
};

export default function ActionApi( props: Props ) {
  const {
    apiAction,
    editable,
    inputs,
    onActionUpdate,
    stepId
  } = props;

  const { config, validationErrors } = useAgentEdit();
  const globalContext = config?.context || {};
  const urlError = validationErrors[stepId]?.action?.url;
  const bodyError = validationErrors[stepId]?.action?.body;
  const [ action, setAction ] = useState<AgentConfigStepApiAction>( apiAction );

  useEffect( () => {
    setAction( apiAction );
  }, [ apiAction ] );

  const onChange = <T,>( key: keyof AgentConfigStepApiAction, value: T ) => {
    const res = {
      ...action,
      [key]: value
    };
    setAction( res );
    onActionUpdate( res );
  };

  const onConvoKeyChange = ( event: React.ChangeEvent<HTMLSelectElement> ) => onChange<string>( 'conversation_key', event.currentTarget.value );
  const onSourceChange = ( event: React.ChangeEvent<HTMLTextAreaElement> ) => onChange<string>( 'body', event.currentTarget.value );
  const onURLChange = ( event: React.ChangeEvent<HTMLInputElement> ) => onChange<string>( 'url', event.currentTarget.value );
  const onMethodChange = ( event: React.ChangeEvent<HTMLSelectElement> ) => onChange<string>( 'method', event.currentTarget.value );
  const onKeyChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const res = { ...action };
    if ( typeof res.headers != 'undefined' ) {
      res.headers[index].header = event.currentTarget.value;
    }
    setAction( res );
    onActionUpdate( res );
  };
  const onValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const res = { ...action };
    if ( typeof res.headers != 'undefined' ) {
      res.headers[index].value = event.currentTarget.value;
    }
    setAction( res );
    onActionUpdate( res );
  };
  const onDeleteClick = ( index: number ) => {
    const res = { ...action };
    if ( typeof res.headers != 'undefined' ) {
      res.headers.splice( index, 1 );
    }
    setAction( res );
    onActionUpdate( res );
  };

  const onAddClick = () => {
    const res = { ...action };
    const blank = { header: 'new', value: 'value' };
    if ( typeof res.headers != 'undefined' ) {
      res.headers.push( blank );
    } else {
      res.headers = [ blank ];
    }
    setAction( res );
    onActionUpdate( res );
  };

  const renderInputVariables = () => {
    return Object.keys( inputs ).map(
      key => (
        <option key={ key } value={ key }>
          {key}
        </option>
      )
    );
  };

  // Create variables array for InputWithTemplateVars
  const inputVariables: TemplateVarItem[] = Object.keys( inputs ).map( key => ( {
    id: key,
    name: key,
    type: 'Inputs'
  } ) );

  const globalContextVariables: TemplateVarItem[] = Object.keys( globalContext ).map( key => ( {
    id: `global.${ key }`,
    name: `global.${ key }`,
    type: 'Global Context'
  } ) );

  const variables = [ ...inputVariables, ...globalContextVariables ];

  return (
    <div className='configPane'>
      <Text as='div' className='paneTitle' size='md' weight='bold'>{ getNodeTypeLabel( AgentConfigStepTypeEnum.API ) } Action</Text>

      <Text as='label' size='sm' htmlFor='url'>URL</Text>
      <InputWithTemplateVars
        data-testid='config-edit-panel-action-api-url-input'
        disabled={ !editable }
        error={ urlError }
        id='url'
        inputType='text'
        onChange={ ( value ) => onChange<string>( 'url', value ) }
        value={ action.url }
        variables={ variables }
      />
      <Text as='label' size='sm' htmlFor='url'>Method</Text>
      <select
        data-testid='config-edit-panel-action-api-method-select'
        disabled={ !editable }
        onChange={ onMethodChange }
        value={ action.method }
      >
        <option value='post'>POST</option>
        <option value='get'>GET</option>
        <option value='put'>PUT</option>
        <option value='delete'>DELETE</option>
      </select>
      {action.method != 'get' && (
        <>
          <Text as='label' size='sm' htmlFor='source'>Body</Text>
          <InputWithTemplateVars
            data-testid='config-edit-panel-action-api-body-textarea'
            disabled={ !editable }
            error={ bodyError }
            id='source'
            inputType='textarea'
            onChange={ ( value ) => onChange<string>( 'body', value ) }
            value={ action.body }
            variables={ variables }
          />
        </>
      )}
      <Text as='label' size='sm' htmlFor='mapConversation'>Map Conversation</Text>
      <select
        data-testid='config-edit-panel-action-api-map-conversation-select'
        disabled={ !editable }
        id='mapConversation'
        onChange={ onConvoKeyChange }
        value={ action.conversation_key }
      >
        <option value='none'>Do Not Include</option>
        {renderInputVariables()}
      </select>
      <Text as='label' size='sm' htmlFor='source'>Headers</Text>
      <div data-testid='config-edit-panel-action-api-add-header-button' className='button' onClick={ onAddClick }>
        <FiPlus />
      </div>
      {
        typeof apiAction.headers != 'undefined' && apiAction.headers.map(
          ( input: AgentConfigStepApiActionHeaders, index: number ) => {
            return (
              <div key={ index } className='flex-row'>
                <div>
                  <input
                    data-testid={ `config-edit-panel-action-api-header-${ index }-key-input` }
                    disabled={ !editable }
                    onChange={ ( event ) => onKeyChange( event, index ) }
                    type='text'
                    value={ input.header }
                  />
                </div>
                <div>
                  <input
                    data-testid={ `config-edit-panel-action-api-header-${ index }-value-input` }
                    disabled={ !editable }
                    onChange={ ( event ) => onValueChange( event, index ) }
                    type='text'
                    value={ input.value }
                  />
                </div>
                {editable && (
                  <div data-testid={ `config-edit-panel-action-api-header-${ index }-delete-button` } className='button'>
                    <FiMinus
                      onClick={
                        editable ? () => onDeleteClick( index ) : undefined
                      }
                    />
                  </div>
                ) }
              </div>
            );
          }
        )
      }
      <br />
    </div>
  );
}
